import { ArrowRight } from 'lucide-react'
import { useNavigate, Link } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import checkIcon from '@/assets/icons/checks.png'
import { useJobs } from '@/lib/api/hooks/job'
import type { Job } from '@/lib/api/types'
import { useAllWebPages } from '@/lib/api/hooks/web-pages'
import FullPageSpinner from '@/components/ui/FullPageSpinner'
import { parseHtmlString } from '@/utils/helpers/html'
import { Heading } from '@/components/ui/Typography'


// Helper function to format date from "2025-11-15" to "15th Nov. 2025"
const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const day = date.getDate()
  const monthNames = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.']
  const month = monthNames[date.getMonth()]
  const year = date.getFullYear()

  // Add ordinal suffix
  const ordinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd']
    const v = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
  }

  return `${ordinal(day)} ${month} ${year}`
}

// Helper function to determine job status based on dates
const getJobStatus = (startDate: string, endDate: string): 'Open' | 'Closed' => {
  const now = new Date()
  const start = new Date(startDate)
  const end = new Date(endDate)

  if (now >= start && now <= end) {
    return 'Open'
  } else {
    return 'Closed'
  }
}

// Helper function to format job type
const formatJobType = (jobType: string): string => {
  const typeMap: Record<string, string> = {
    'FULL_TIME': 'Fulltime',
    'PER_TIME': 'Part-time',
    'PART_TIME': 'Part-time',
    'CONTRACT': 'Contract',
    'INTERNSHIP': 'Internship'
  }
  return typeMap[jobType] || jobType
}

interface WhyJoinCard {
  id: string
  number: string
  title: string
  description: string
}

interface EDICard {
  id: string
  number: string
  title: string
  description: string
}

const Careers = () => {
  const { data, isFetching, isError, refetch } = useAllWebPages();
  const careersData = data?.data && data?.data.find((page) =>
    page.codeName?.toLowerCase() === 'careers' ||
    page.websitePageName?.toLowerCase() === 'careers'
  )?.content || null;

  const navigate = useNavigate()

  // Fetch latest jobs from API (first 3)
  const { data: jobsData, isLoading: jobsLoading } = useJobs({ page: 1, size: 3 }, {})

  // Transform API jobs to component format
  const jobListings = jobsData?.data?.data?.map((job: Job) => {
    const status = getJobStatus(job.startDate, job.endDate)
    return {
      id: job.jobId,
      title: job.jobTitle,
      location: job.jobLocation,
      type: formatJobType(job.jobType),
      organization: 'The African Union Commission', // Not in API, using default
      postingDate: job.datePublished ? formatDate(job.datePublished) : formatDate(job.startDate),
      endDate: formatDate(job.endDate),
      status,
      href: `/opportunities/careers/${job.jobId}`
    }
  }) || []

  const whyJoinCards: WhyJoinCard[] = [
    {
      id: '1',
      number: '1',
      title: `${careersData?.CAREER_BE_PART_OF_SPACE_REVOLUTION?.title ?? "Be Part of Africa's Space Revolution"}`,
      description: `${careersData?.CAREER_BE_PART_OF_SPACE_REVOLUTION?.description ?? 'Join AfSA in driving Africa\'s vision to harness space technology for growth, resilience, and unity across the continent. You\'ll be at the forefront of transformative projects that tackle critical challenges and impact millions.'}`
    },
    {
      id: '2',
      number: '2',
      title: `${careersData?.CAREER_COLLABORATE_WITH_INDUSTRY_LEADER?.title ?? "Collaborate with Industry Leaders"}`,
      description: `${careersData?.CAREER_COLLABORATE_WITH_INDUSTRY_LEADER?.description ?? 'Work alongside top experts from international space agencies, NewSpace companies, and African governments. At AfSA, you\'ll gain invaluable experience and connections as you contribute to Africa\'s ambitious space initiatives.'}`
    },
    {
      id: '3',
      number: '3',
      title: `${careersData?.CAREER_EMPOWER_AFRICA_THROUGH_TECHNOLOGY?.title ?? "Empower Africa Through Technology"}`,
      description: `${careersData?.CAREER_EMPOWER_AFRICA_THROUGH_TECHNOLOGY?.description ?? "Our mission is to leverage space for sustainable development and open up new opportunities for Africa's future. By joining us, you're helping to build a future where technology creates lasting positive change across the continent."}`
    }
  ]

  const ediCards: EDICard[] = [
    {
      id: '1',
      number: '1',
      title: `${careersData?.CAREER_DIVERSITY_DRIVES_INNOVATION?.title ?? ""}`,
      description: `${careersData?.CAREER_DIVERSITY_DRIVES_INNOVATION?.description ?? 'AfSA believes that a diverse team boosts creativity and strengthens its ability to serve Africa\'s varied communities.'}`
    },
    {
      id: '2',
      number: '2',
      title: `${careersData?.CAREER_INCLUSION_AND_EQUITY_MATTER?.title ?? "Inclusion and Equality Matter"}`,
      description: `${careersData?.CAREER_INCLUSION_AND_EQUITY_MATTER?.description ?? "The agency is committed to building an inclusive workplace where all identities and experiences are respected and empowered."}`
    },
    {
      id: '3',
      number: '3',
      title: `${careersData?.CAREER_OPEN_TO_ALL_TALENT?.title ?? "Open to All Talent"}`,
      description: `${careersData?.CAREER_OPEN_TO_ALL_TALENT?.description ?? "AfSA welcomes individuals from all backgrounds and is dedicated to equal opportunities and inclusive practices across the board."}`
    }
  ]

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Careers', href: '/opportunities/careers', active: true }
  ]

  if (isFetching) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Error loading page.</p>
      <button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
    </div>;
  }

  if (!careersData) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Page content not found.</p>
    </div>;
  }

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 ">
      {/* Breadcrumb */}
      <div className=" mb-5">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Hero Image */}
      <div className="mb-20">
        <img
          src={careersData?.CAREER_BANNER?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1756908133/89182d466e4157c7c08b4e728341f009c951f842_gdaynu.png"}
          alt="African Space Agency team members working together on space technology projects"
          className="w-full h-64 sm:h-80 md:h-96 lg:h-[480px] object-cover object-center"
        />
      </div>

      {/* Main Content */}
      <div className="space-y-20">
        {/* Why Join AFSA Section */}
        <div className="flex flex-col items-start gap-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900">
            {careersData?.CAREER_WHY_JOIN_AFSA?.title ?? "Why Join AfSA?"}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {whyJoinCards.map((card) => (
              <div key={card.id} className="px-8 py-6 bg-gray-50 space-y-8">
                <div className="w-12 h-12 relative">
                  <div className="w-12 h-12 bg-wine-200 "></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl sm:text-3xl font-normal font-pt-serif text-white">
                      {card.number}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                    {card.title}
                  </h3>
                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    <span dangerouslySetInnerHTML={parseHtmlString(card.description)} />
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Available Jobs Section */}
        <div className="space-y-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900">
              Available Jobs
            </h2>
            <button
              onClick={() => navigate('/opportunities/careers/jobs')}
              className="px-4 py-2.5 flex items-center gap-2 hover:bg-gray-50 transition-colors"
            >
              <span className="text-base font-medium font-satoshi text-gray-900">
                View all
              </span>
              <ArrowRight className="w-6 h-6 text-gray-900" />
            </button>
          </div>

          <div className="max-w-4xl space-y-0 border border-gray-300 rounded-md overflow-hidden">
            {jobsLoading ? (
              <div className="px-6 py-6 bg-white">
                <div className="space-y-4">
                  <div className="h-6 bg-gray-100 animate-pulse rounded w-3/4" />
                  <div className="h-4 bg-gray-100 animate-pulse rounded w-full" />
                </div>
              </div>
            ) : jobListings.length === 0 ? (
              <div className="px-6 py-6 bg-white text-center">
                <p className="text-gray-600">No jobs available at the moment.</p>
              </div>
            ) : (
              jobListings.map((job, index) => (
                <Link
                  key={job.id}
                  to={job.href}
                  className={`block px-6 py-6 hover:bg-gray-50 transition-colors ${index === 0 ? 'bg-white' : index === jobListings.length - 1 ? 'bg-white' : 'bg-white border-l border-r border-b border-gray-300'
                    }`}
                >
                  <div className="flex justify-between items-start gap-6">
                    <div className="flex-1 space-y-4">
                      <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                        {job.title}
                      </h3>

                      <div className="flex flex-wrap items-center gap-2.5">
                        <div className="flex items-center gap-1">
                          <span className="text-base font-medium font-satoshi text-wine-100">Location:</span>
                          <span className="text-base font-normal font-satoshi text-gray-900">{job.location}</span>
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-700 rounded-full"></div>

                        <div className="flex items-center gap-1">
                          <span className="text-base font-medium font-satoshi text-wine-100">Type:</span>
                          <span className="text-base font-normal font-satoshi text-gray-900">{job.type}</span>
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-700 rounded-full"></div>

                        <div className="flex items-center gap-1">
                          <span className="text-base font-medium font-satoshi text-wine-100">Organization:</span>
                          <span className="text-base font-normal font-satoshi text-gray-900">{job.organization}</span>
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-700 rounded-full"></div>

                        <div className="flex items-center gap-1">
                          <span className="text-base font-medium font-satoshi text-wine-100">Posting date:</span>
                          <span className="text-base font-normal font-satoshi text-gray-900">{job.postingDate}</span>
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-700 rounded-full"></div>

                        <div className="flex items-center gap-1">
                          <span className="text-base font-medium font-satoshi text-wine-100">End date:</span>
                          <span className="text-base font-normal font-satoshi text-gray-900">{job.endDate}</span>
                        </div>
                      </div>
                    </div>

                    <div className={`px-4 py-1 rounded-md flex items-center gap-1.5 ${job.status === 'Open' ? 'bg-emerald-50' : 'bg-wine-100/10'
                      }`}>
                      <div className={`w-2 h-2 rounded-full ${job.status === 'Open' ? 'bg-forestGreen-100' : 'bg-wine-100'
                        }`}></div>
                      <span className={`text-sm font-medium font-satoshi ${job.status === 'Open' ? 'text-forestGreen-100' : 'text-wine-100'
                        }`}>
                        {job.status}
                      </span>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* A Career with Purpose and Innovation */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-12">
            <div className="space-y-4">
              <div className="px-4 py-2 bg-wine-100 rounded-full inline-block">
                <span className="text-sm font-medium font-satoshi text-white">
                  What we Offer
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900">
                {careersData?.CAREER_WHAT_WE_OFFER?.sectionTitle ?? "A Career with Purpose and Innovation"}
              </h2>
            </div>

            <div className="space-y-8">
              {
                Array.isArray(careersData?.CAREER_WHAT_WE_OFFER?.listOfPurpose) && careersData.CAREER_WHAT_WE_OFFER.listOfPurpose.length > 0 ? careersData.CAREER_WHAT_WE_OFFER.listOfPurpose.map((item: string, index: number) => (
                  <div key={index} className="flex items-start gap-4">
                    <img src={checkIcon} alt="Check" className="w-10 h-10 flex-shrink-0" />
                    <p className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                      {item}
                    </p>
                  </div>
                )) : null

              }

            </div>
          </div>

          <div className="flex justify-center">
            <img
              src={careersData?.CAREER_BANNER_2?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760297/0419d7b9498659c65e546b484c3a84dbb59d1976_2_grc2la.png"}
              alt="African space technology professionals collaborating on innovative projects"
              className="w-full max-w-[624px] h-auto object-cover rounded"
            />
          </div>
        </div>

        {/* Shaping Africa's Future Through Space */}
        <div className="px-8 2xl:px-16 py-16 bg-wine-100 space-y-12">
          <div className="space-y-4">
            <div className="px-4 py-2 bg-wine-200 rounded-full inline-block">
              <span className="text-sm font-medium font-satoshi text-white">
                Our Goal
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-white max-w-md">
              {careersData?.CAREER_OUR_GOALS?.title ?? "Shaping Africa's Future Through Space"}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 2xl:gap-20">
            {Array.isArray(careersData?.CAREER_OUR_GOALS?.listOfPurpose) && careersData.CAREER_OUR_GOALS.listOfPurpose.length > 0 ? (
              careersData.CAREER_OUR_GOALS.listOfPurpose.map((goal: string, index: number) => (
                <div key={index} className="flex gap-6 border-l-[0.375rem] pl-6 border-l-gray-300">
                  <div className="space-y-4">
                    <p className="text-lg font-normal font-satoshi text-white leading-relaxed">
                      {goal}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <>
                <div className="flex gap-6 border-l-[0.375rem] pl-6 border-l-gray-300">
                  <div className="space-y-4">
                    <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-white leading-7">
                      Use Earth Observation to Drive Sustainable Impact
                    </h3>
                    <p className="text-lg font-normal font-satoshi text-white leading-relaxed">
                      Imagine working on projects that combat deforestation, monitor climate change, and protect natural resources. At AfSA, you'll leverage satellite technology to help Africa's cities grow sustainably and its ecosystems thrive.
                    </p>
                  </div>
                </div>
                <div className="flex gap-6 border-l-[0.375rem] pl-6 border-l-gray-300">
                  <div className="space-y-4">
                    <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-white leading-7">
                      Bridge the Digital Divide Across Africa
                    </h3>
                    <p className="text-lg font-normal font-satoshi text-white leading-relaxed">
                      Satellite connectivity isn't just about technology—it's about bringing vital services to people. Whether it's improving broadband access, supporting telemedicine, or enabling distance learning in remote areas, your role with AfSA will make life-changing connections possible.
                    </p>
                  </div>
                </div>
                <div className="flex gap-6 border-l-[0.375rem] pl-6 border-l-gray-300">
                  <div className="space-y-4">
                    <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-white leading-7">
                      Advance Space Science and Navigation for a Better Africa
                    </h3>
                    <p className="text-lg font-normal font-satoshi text-white leading-relaxed">
                      From studying the cosmos to developing systems that support safe navigation and environmental monitoring, your work will push the boundaries of knowledge and innovation. At AfSA, you'll play a part in building Africa's space science capabilities.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Equality, Diversity, and Inclusion */}
        <div className="space-y-6 ">
          <Heading level={3} className="max-w-xl">
            {careersData?.CAREER_EQUITY_DIVERSITY_INCLUSION?.title ?? "Equality, Diversity, and Inclusion"}
          </Heading>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" >
            {ediCards.map((card) => (
              <div key={card.id} className="px-8 py-6 bg-gray-50 space-y-8">
                

                <div className="space-y-4">
                  <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                    {card.title}
                  </h3>
                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    <span dangerouslySetInnerHTML={parseHtmlString(card.description)} />
                  </p>
                </div>
              </div>
            ))}
          </div>               

{/* This is updated to use only one card */}
                {/* <div className="bg-gray-50 flex flex-col gap-6 p-4 md:p-8 rounded xl:p-16 xl:gap-8">
                  <Heading level={5} className="">
                    {ediCards[0].title}
                  </Heading>
                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    <span dangerouslySetInnerHTML={parseHtmlString(ediCards[0].description)} />
                  </p>
                </div> */}
        </div>
      </div>
    </div>
  )
}

export default Careers